import React, {
  forwardRef,
  useCallback,
  useRef,
  ComponentProps as PropsOf,
  ForwardedRef,
  ForwardRefExoticComponent,
  HTMLProps, useState, SyntheticEvent,
} from 'react';

import { useRefs } from '@americanexpress/react-use-hooks';
import ProgressBar from './ProgressBar';
import type { Props as VideoProps } from './Video';
import Video from './Video';
import { noop } from '../../utils';
import useTimedCallback from '../../hooks/FutureUHL/timers/useTimedCallback';
import useOnLoad from '../../hooks/FutureUHL/useOnLoad';
import {
  DEFAULT_VIDEO_TIMEOUT_SEC,
  PROGRESS_BAR_UPDATE_INTERVAL_SEC as UPDATE_SEC,
} from './constants';
import styles from './ShortSilentVideoModal.scss';

type SpecificProps = {
  url?: string,
  onStart?: (video: HTMLVideoElement) => void,
  onPlayError?: (error: Error) => void,
  videoTimeoutSec?: number,
  forBar?: Omit<Partial<PropsOf<typeof ProgressBar>>, 'getNext'>,
  forContainer?: Partial<HTMLProps<HTMLDivElement>>,
  onTimeout?: () => void,
};

type Props = SpecificProps & Omit<VideoProps, keyof SpecificProps>;

const ShortSilentVideo: ForwardRefExoticComponent<Props> = forwardRef(({
  url,
  onStart: onStartProp = noop,
  onTimeout: onTimeoutProp = noop,
  onPlayError = noop,
  videoTimeoutSec = DEFAULT_VIDEO_TIMEOUT_SEC,
  autoPlay,
  forBar,
  forContainer,
  preload = 'auto',
  ...videoProps
}, forwardedRef: ForwardedRef<HTMLVideoElement>) => {
  const [timedOut, setTimedOut] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const videoStartedRef = useRef(false);
  const dualVideoRefs = useRefs(videoRef, ...forwardedRef ? [forwardedRef] : []);

  const onTimeout = useCallback(() => {
    setTimedOut(true);
    onTimeoutProp();
  }, [onTimeoutProp]);
  const {
    start: startVideoLoadTimerError,
    stop: cancelVideoTimeoutError,
  } = useTimedCallback(onTimeout, videoTimeoutSec);
  useOnLoad(startVideoLoadTimerError);

  const handlePlayVideo = useCallback((event: SyntheticEvent<HTMLVideoElement, Event>) => {
    if (!timedOut && autoPlay) {
      event.currentTarget.play()
        .catch((error: Error) => {
          cancelVideoTimeoutError();
          onPlayError(error);
        });
    }
  }, [autoPlay, cancelVideoTimeoutError, onPlayError, timedOut]);

  const onStart = useCallback((video) => {
    cancelVideoTimeoutError();
    onStartProp(video);
  }, [cancelVideoTimeoutError, onStartProp]);

  const getNextProgress = useCallback(() => {
    if (!videoRef.current?.duration || !videoRef.current?.currentTime) {
      /* This avoids null videoRefs, but also keeps nextProgress at 0 until the video starts.
       * Video '*play*' events don't fire and 'load*' events only fire if the video isn't cached,
       * leaving no reliable way to know when this video starts. But, the bar catches up. */
      return 0;
    }
    const { currentTime, duration } = videoRef.current;

    if (currentTime > 0 && !timedOut && !videoStartedRef.current) {
      videoStartedRef.current = true;
      onStart(videoRef.current);
    }

    return Math.min(100, ((currentTime + UPDATE_SEC) / duration) * 100);
  }, [onStart, timedOut, videoStartedRef, videoRef]);

  return (
    <div className="position-static" {...forContainer || {}}>
      {url && !timedOut && (
        <>
          <Video
            muted={true}
            ref={dualVideoRefs}
            data-testid="video"
            preload={preload}
            autoPlay={autoPlay}
            playsInline={true}
            webkit-playsinline={true}
            controls={false}
            onCanPlay={handlePlayVideo}
            {...videoProps}
          >
            <source src={url} type="video/mp4" />
          </Video>
          <ProgressBar
            getNext={getNextProgress}
            className={`position-absolute ${styles.bottomLeft}`}
            updateSec={UPDATE_SEC}
            {...forBar || {}}
          />
        </>
      )}
    </div>
  );
});
ShortSilentVideo.displayName = 'ShortSilentVideo';

export default ShortSilentVideo;
